/**
 * MyProfile — the signed-in member's own profile: info card, content tabs and
 * the entry points to Update Profile, View Portrait and the photo picker.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  calculateWeightProgress,
  formatWeightInUnit,
  normalizeWeightUnit,
} from '@wlb/shared/utils/weightUtils';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { FlameIcon, TrendingDownIcon, TargetIcon } from '../components/ui/icons';
import { getProfileAvatarUrl } from '../features/profile/useCurrentProfile';
import { useProfileTabs } from '../features/profile/useProfileTabs';
import { ProfileInfoCard } from '../features/profile/ProfileInfoCard';
import { ProfileCardSkeleton } from '../features/profile/ProfileSkeletons';
import { ProfilePostList } from '../features/profile/ProfilePostList';
import { AvatarUploadModal } from '../features/profile/AvatarUploadModal';
import { EMPTY_MESSAGE_KEYS } from '../features/profile/profileTabContent';
import '../features/profile/profile.css';
import '../features/profile/updateProfile.css';

const VIEWER = { isSelf: true } as const;

export function MyProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, loading, error } = useProfile();
  const { items, activeKey, setActiveKey, action, posts } = useProfileTabs(
    profile?.userId,
    true,
    profile?.userGuid,
  );
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  const unit = normalizeWeightUnit(profile?.unit);
  const weightProgress = useMemo(() => {
    const start = profile?.startWeight;
    const current = profile?.currentWeight;
    const goal = profile?.goalWeight;
    const percent = calculateWeightProgress(start, current, goal);
    return {
      start: start != null ? formatWeightInUnit(start, unit) : '--',
      current: current != null ? formatWeightInUnit(current, unit) : '--',
      goal: goal != null ? formatWeightInUnit(goal, unit) : '--',
      percent,
    };
  }, [profile, unit]);

  if (!profile) {
    return loading || !error ? (
      <ProfileCardSkeleton />
    ) : (
      <p className="profile-posts__status profile-posts__status--error" role="alert">
        {t('profile.toast.unableToLoadProfile')}
      </p>
    );
  }

  return (
    <section className="profile-page" aria-label={t('profile.header.title')}>
      <ProfileInfoCard
        profile={profile}
        viewer={VIEWER}
        onChangePhoto={() => setPhotoModalOpen(true)}
        onSelectStat={key => navigate(`/profile/connections?tab=${key}`)}
        actions={
          <>
            <Button onClick={() => navigate('/profile/edit')}>
              {t('profile.buttons.updateProfile')}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/profile/portrait')}>
              {t('profile.buttons.viewPortrait')}
            </Button>
          </>
        }
      />

      {/* Weight progress card (mobile parity) */}
      <div className="profile-edit__weight-card">
        <h3 className="profile-edit__weight-title">{t('profile.weightProgress.title')}</h3>
        <p className="profile-edit__weight-subtitle">{t('profile.weightProgress.subtitle')}</p>
        <div className="profile-edit__weight-row">
          <div className="profile-edit__weight-col">
            <span className="profile-edit__weight-dot profile-edit__weight-dot--start" aria-hidden />
            <span className="profile-edit__weight-label">{t('profile.weightProgress.start')}</span>
            <span className="profile-edit__weight-value-row">
              <span className="profile-edit__weight-value">{weightProgress.start}</span>
              <FlameIcon size={18} className="profile-edit__weight-icon" />
            </span>
          </div>
          <div className="profile-edit__weight-col">
            <span className="profile-edit__weight-dot profile-edit__weight-dot--current" aria-hidden />
            <span className="profile-edit__weight-label">{t('profile.weightProgress.current')}</span>
            <span className="profile-edit__weight-value-row">
              <span className="profile-edit__weight-value">{weightProgress.current}</span>
              <TrendingDownIcon size={18} className="profile-edit__weight-icon" />
            </span>
          </div>
          <div className="profile-edit__weight-col">
            <span className="profile-edit__weight-dot profile-edit__weight-dot--goal" aria-hidden />
            <span className="profile-edit__weight-label">{t('profile.weightProgress.goal')}</span>
            <span className="profile-edit__weight-value-row">
              <span className="profile-edit__weight-value">{weightProgress.goal}</span>
              <TargetIcon size={18} className="profile-edit__weight-icon" />
            </span>
          </div>
        </div>
        <div className="profile-edit__progress-header">
          <span className="profile-edit__progress-label">{t('profile.weightProgress.progressLabel')}</span>
          <span
            className={`profile-edit__progress-percent${weightProgress.percent >= 100 ? ' profile-edit__progress-percent--done' : ''}`}
          >
            {Math.round(weightProgress.percent)}%
          </span>
        </div>
        <div
          className="profile-edit__progress-bar"
          role="progressbar"
          aria-valuenow={Math.round(weightProgress.percent)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="profile-edit__progress-fill"
            style={{ width: `${Math.min(weightProgress.percent, 100)}%` }}
          />
        </div>
      </div>

      <Tabs
        items={items}
        activeKey={activeKey}
        onChange={setActiveKey}
        label={t('profile.accessibility.profileTabs')}
        idPrefix="profile-tab"
      />

      <div
        role="tabpanel"
        id={`profile-tab-panel-${activeKey}`}
        aria-labelledby={`profile-tab-${activeKey}`}
      >
        {action ? (
          <ProfilePostList
            posts={posts}
            viewerProfile={profile}
            emptyMessage={t(EMPTY_MESSAGE_KEYS[activeKey])}
          />
        ) : (
          <p className="profile-posts__empty">{t('profile.empty.activities')}</p>
        )}
      </div>

      {photoModalOpen && (
        <AvatarUploadModal
          currentAvatarUrl={getProfileAvatarUrl(profile)}
          onClose={() => setPhotoModalOpen(false)}
        />
      )}
    </section>
  );
}
